import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  ModeleSavCentre,
  SavOrigineCode,
  SavOrigineResultat,
} from '../../../../components/sav/Models'
import {rechercherSavOrigine, savErreur} from '../../../../components/sav/requests'
import {formatSavDate, formatSavMontant} from '../../../../components/sav/libelles'

export interface SavVenteHorsKoena {
  origine: Exclude<SavOrigineCode, 'KOENA_CMD' | 'KOENA_B2B'>
  refFactureExterne?: string
  refCommandeExterne?: string
  plateforme?: string
  refTicketOdoo?: string
  dateAchat?: string
  dateAchatVerifiee: boolean
  clientHistoriqueFacturation?: number
  centre?: number
  justificatifs: Array<File>
  commentaire?: string
}

type Props = {
  nomClient: string
  centres: Array<ModeleSavCentre>
  initiale?: SavVenteHorsKoena | null
  onClose: () => void
  onRattacher: (vente: SavVenteHorsKoena) => void
}

const CARTES: Array<{
  origine: SavVenteHorsKoena['origine']
  titre: string
  description: string
  obligatoire: string
}> = [
  {
    origine: 'ATEMO',
    titre: 'Vente ATEMO',
    description: 'Comptoir ou ancien flux facturé dans ATEMO',
    obligatoire: 'N° facture ou commande ATEMO',
  },
  {
    origine: 'PLATEFORME',
    titre: 'Plateforme',
    description: 'Commande passée sur une marketplace',
    obligatoire: 'Plateforme et n° de commande',
  },
  {
    origine: 'HISTO_ODOO',
    titre: 'Historique ODOO',
    description: "Ticket ODOO non repris par l'import",
    obligatoire: 'N° de ticket ODOO',
  },
  {
    origine: 'LIBRE',
    titre: 'Libre',
    description: 'Aucune référence retrouvée',
    obligatoire: 'Justificatif ou validation du responsable SAV',
  },
]

// Écran 3 : rattacher une vente que KOENA ne connaît pas
const SavVenteHorsKoenaModal = ({nomClient, centres, initiale, onClose, onRattacher}: Props) => {
  const [vente, setVente] = useState<SavVenteHorsKoena>(
    initiale ?? {origine: 'ATEMO', dateAchatVerifiee: false, justificatifs: []}
  )
  const [recherche, setRecherche] = useState('')
  const [factures, setFactures] = useState<Array<SavOrigineResultat> | null>(null)

  const maj = (changement: Partial<SavVenteHorsKoena>) => setVente((v) => ({...v, ...changement}))

  const chercher = () => {
    rechercherSavOrigine(recherche)
      .then((resultats) => setFactures(resultats.filter((r) => r.origine === 'ATEMO')))
      .catch((e) => toast.error(savErreur(e)))
  }

  const choisirFacture = (facture: SavOrigineResultat) =>
    maj({
      origine: 'ATEMO',
      clientHistoriqueFacturation: facture.id,
      refFactureExterne: facture.numero,
      dateAchat: facture.date ?? undefined,
      dateAchatVerifiee: true,
    })

  const erreur = (() => {
    switch (vente.origine) {
      case 'ATEMO':
        return vente.refFactureExterne || vente.refCommandeExterne
          ? null
          : 'Le n° de facture ou de commande ATEMO est obligatoire.'
      case 'PLATEFORME':
        return vente.plateforme && vente.refCommandeExterne
          ? null
          : 'La plateforme et le n° de commande sont obligatoires.'
      case 'HISTO_ODOO':
        return vente.refTicketOdoo ? null : 'Le n° de ticket ODOO est obligatoire.'
      default:
        return null
    }
  })()

  const champ = (libelle: string, cle: keyof SavVenteHorsKoena, type = 'text', requis = false) => (
    <div className='col-md-6 mb-4'>
      <label className={`form-label ${requis ? 'required' : ''}`}>{libelle}</label>
      <input
        type={type}
        className='form-control form-control-solid'
        value={(vente[cle] as string) ?? ''}
        onChange={(e) =>
          maj({
            [cle]: e.target.value,
            ...(cle === 'dateAchat' ? {dateAchatVerifiee: false} : {}),
          } as Partial<SavVenteHorsKoena>)
        }
      />
    </div>
  )

  return (
    <Modal show onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex w-100 justify-content-between pe-5'>
          <span>Rattacher une vente hors KOENA</span>
          <span className='fs-6'>
            SAV <span className='text-success'>#nouveau</span> · {nomClient}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className='form-label'>Rechercher dans l'historique de facturation</label>
        <div className='input-group mb-3'>
          <input
            className='form-control form-control-solid'
            placeholder='N° de facture ou de commande'
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && chercher()}
          />
          <button
            className='btn btn-light-primary'
            onClick={chercher}
            disabled={recherche.trim().length < 3}
          >
            Rechercher
          </button>
        </div>
        {factures && !factures.length && (
          <div className='alert alert-warning'>Aucune facture ATEMO trouvée.</div>
        )}
        {factures?.map((f) => (
          <div
            key={f.id}
            className={`alert ${
              vente.clientHistoriqueFacturation === f.id ? 'alert-success' : 'alert-secondary'
            } d-flex justify-content-between align-items-center`}
          >
            <span>
              Facture ATEMO <strong>{f.numero}</strong> du {formatSavDate(f.date)} ·{' '}
              {formatSavMontant(f.montant)} HT
              {f.client && ` · ${f.client.nom}`}. Date d'achat et prix seront vérifiés.
            </span>
            <button className='btn btn-sm btn-success' onClick={() => choisirFacture(f)}>
              Choisir
            </button>
          </div>
        ))}

        <div className='row g-3 my-4'>
          {CARTES.map((carte) => (
            <div className='col-md-3' key={carte.origine}>
              <div
                className='rounded p-4 h-100 cursor-pointer'
                style={{
                  border: `2px solid ${vente.origine === carte.origine ? '#50CD89' : '#E1E3EA'}`,
                  backgroundColor: vente.origine === carte.origine ? '#E8FFF3' : '#FFFFFF',
                }}
                onClick={() => maj({origine: carte.origine})}
              >
                <div className='fw-bold'>{carte.titre}</div>
                <div className='text-muted fs-7 my-1'>{carte.description}</div>
                <div className='fs-8 text-gray-600'>{carte.obligatoire}</div>
              </div>
            </div>
          ))}
        </div>

        <div className='row'>
          {vente.origine === 'ATEMO' && (
            <>
              {champ('N° facture ATEMO', 'refFactureExterne', 'text', !vente.refCommandeExterne)}
              {champ('N° commande ATEMO', 'refCommandeExterne')}
            </>
          )}
          {vente.origine === 'PLATEFORME' && (
            <>
              {champ('Plateforme', 'plateforme', 'text', true)}
              {champ('N° de commande plateforme', 'refCommandeExterne', 'text', true)}
            </>
          )}
          {vente.origine === 'HISTO_ODOO' &&
            champ('N° de ticket ODOO', 'refTicketOdoo', 'text', true)}
          {champ("Date d'achat", 'dateAchat', 'date')}
          <div className='col-md-6 mb-4'>
            <label className='form-label'>Centre de la vente</label>
            <select
              className='form-select'
              value={vente.centre ?? ''}
              onChange={(e) => maj({centre: e.target.value ? Number(e.target.value) : undefined})}
            >
              <option value=''>Choisir</option>
              {centres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.libelle}
                </option>
              ))}
            </select>
            <div className='form-text'>Centre qui a vendu la pièce</div>
          </div>
          <div className='col-12 mb-4'>
            <label className={`form-label ${vente.origine === 'LIBRE' ? 'required' : ''}`}>
              Justificatif (facture, bon de livraison, photo)
            </label>
            <input
              type='file'
              multiple
              accept='image/*,application/pdf'
              className='form-control'
              onChange={(e) => maj({justificatifs: Array.from(e.target.files ?? [])})}
            />
            {vente.origine === 'LIBRE' && (
              <div className='text-muted fs-7 mt-1'>
                Sans justificatif, le responsable SAV devra valider le dossier avant la
                qualification.
              </div>
            )}
          </div>
          <div className='col-12'>
            <label className='form-label'>Commentaire interne</label>
            <input
              className='form-control form-control-solid'
              value={vente.commentaire ?? ''}
              onChange={(e) => maj({commentaire: e.target.value})}
            />
          </div>
        </div>
        {erreur && <div className='text-danger mt-3'>{erreur}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button className='btn btn-light' onClick={onClose}>
          Fermer
        </button>
        <button
          className='btn btn-light-primary'
          disabled={!!erreur}
          onClick={() => onRattacher(vente)}
        >
          Rattacher la vente
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default SavVenteHorsKoenaModal
